import { Provider } from 'mobx-react'
import { Suspense, useState } from 'react'
import { RouterProvider } from 'react-router'
import { observer } from 'mobx-react-lite'
import { ConfigProvider, theme } from 'antd'

import 'antd/dist/reset.css'
import './index.css'

import store from './stores'
import { router } from './router'
import Maintenance from './pages/Maintenance'
import Loading from './components/Loading'
import NightSky from './components/NightSky'

/**
 * Ant Design is still used for toasts, Upload, Modal and Avatar.
 * Its theme maps onto the YWC21 design-system tokens (theme "night").
 * antd needs literal colour values, so these mirror src/index.css.
 */
const antdDesignSystemTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    fontFamily: `'Prompt', 'Noto Sans Thai', system-ui, sans-serif`,
    colorPrimary: '#f0d590', // accent
    colorTextLightSolid: '#192851', // on-accent
    colorInfo: '#f0d590',
    colorLink: '#e0d69a', // ink
    colorText: '#dfe4f2', // ink-body
    colorTextSecondary: '#b9c2d9', // ink-muted
    colorTextPlaceholder: '#b9c2d9',
    colorBgBase: '#192851', // surface
    colorBgContainer: '#213263', // surface-raised
    colorBgElevated: '#213263',
    colorBorder: 'rgba(185, 194, 217, 0.7)', // ink-muted/70
    colorBorderSecondary: 'rgba(224, 214, 154, 0.35)', // line
    colorError: '#ffb4a8', // danger (not in the design system yet)
    borderRadius: 4, // radius-sm
    borderRadiusLG: 6, // radius-md
    controlHeight: 44,
  },
}

// Static antd APIs (message.success, Modal.confirm…) render outside the React
// tree, so they need the same theme passed explicitly.
ConfigProvider.config({
  holderRender: children => (
    <ConfigProvider theme={antdDesignSystemTheme}>{children}</ConfigProvider>
  ),
})

const App: React.FC = () => {
  const [maintenance] = useState(false)

  return (
    <ConfigProvider theme={antdDesignSystemTheme}>
      <Provider store={store}>
        {import.meta.env.VITE_APP_TARGET === 'staging' && (
          <div className="pointer-events-none fixed left-1/2 top-1 z-[9999] -translate-x-1/2 rounded-full bg-red-600 px-4 py-1 text-xs font-bold text-white shadow-md">
            ใช้เพื่อการทดสอบภายในเท่านั้น ห้ามเผยแพร่
          </div>
        )}

        <NightSky />

        <Suspense fallback={<Loading />}>
          {maintenance ? <Maintenance /> : <RouterProvider router={router} />}
        </Suspense>
      </Provider>
    </ConfigProvider>
  )
}

export default observer(App)
